import { useRef, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation, Pagination } from "swiper/modules";
import { useInView } from "react-intersection-observer";
import { useNavigate } from "react-router-dom";

import cookie from "./image-3.png";
import cake from "./cake2.png";
import cake1 from "./cake.png";

import img from "./image.png";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

// Lazy Image Component
const LazyImage = ({ src, alt, onLoad, isSmaller = false, ...props }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const imgRef = useRef();

  const { ref: lazyRef, inView: imageInView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const handleLoad = useCallback(() => {
    setIsLoaded(true);
    if (onLoad) onLoad();
  }, [onLoad]);

  const setRefs = useCallback(
    (node) => {
      imgRef.current = node;
      lazyRef(node);
    },
    [lazyRef]
  );

  return (
    <div 
      ref={setRefs} 
      className={`relative w-full h-full flex items-center justify-center ${
        isSmaller ? 'scale-75' : 'scale-100'
      }`}
    >
      {!isLoaded && (
        <div className="absolute inset-0 bg-gray-200 animate-pulse rounded" />
      )}
      {imageInView && (
        <img
          src={src}
          alt={alt}
          className={`max-w-full max-h-full object-contain transition-opacity duration-300 ${
            isLoaded ? "opacity-100" : "opacity-0"
          }`}
          onLoad={handleLoad}
          loading="lazy"
          {...props}
        />
      )}
    </div>
  );
};

export default function HeroSection() {
  const swiperRef = useRef(null);
  const navigate = useNavigate();

  const heroImages = [
    { id: 1, image: "https://cdn.acsdev.in/iynacakes/cake2.png", alt: "Delicious sponge cake with strawberries" },
    { id: 2, image: "https://cdn.acsdev.in/iynacakes/qwer.png", alt: "Chocolate layered cake" },
    { id: 3, image: "https://cdn.acsdev.in/iynacakes/image.png", alt: "Freshly baked cookie with chocolate chips" },
  ];

  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });

  const handleShopNowClick = () => {
    navigate("/menu");
  };

  return (
    <section
      ref={ref}
      className="relative w-full bg-[#FEF7F1]  px-2 py-2 lg:py-10 sm:px-8 lg:px-10 overflow-hidden
                 grid grid-cols-1 lg:grid-cols-3 grid-rows-[auto_1fr_auto] lg:grid-rows-1 gap-y-6 sm:gap-y-10"
    >
      {/* Decorative ellipse */}
      <div className="absolute left-1/2 top-[320px] w-[250px] h-[450px] md:w-[350px] md:h-[600px]
                      bg-[#FFE0D1] rounded-full -translate-x-1/2 -translate-y-1/2 rotate-[45deg] opacity-70" />

      {/* Navigation buttons - positioned at section edges */}
      <button className="hero-prev absolute left-2 sm:left-4 lg:left-8 top-1/2 -translate-y-1/2 text-5xl sm:text-6xl lg:text-8xl font-bold text-cyan-900 hover:text-red-500 z-30 hidden lg:block">
        ‹
      </button>
      <button className="hero-next absolute right-2 sm:right-4 lg:right-8 top-1/2 -translate-y-1/2 text-5xl sm:text-6xl lg:text-8xl font-bold text-cyan-900 hover:text-red-500 z-30 hidden lg:block">
        ›
      </button>

      {/* Baked Heading */}
      <motion.h1
        className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-cyan-900 leading-tight
                   mb-4 sm:mb-6 lg:mb-0 lg:col-start-1 lg:row-start-1 lg:justify-self-end lg:translate-x-0 sm:translate-x-4 lg:translate-x-8 text-center sm:text-left"
        style={{ fontFamily: "'Comic Sans MS', cursive" }}
        initial={{ opacity: 0, x: -50 }}
        animate={inView ? { opacity: 1, x: 0 } : {}}
        transition={{ duration: 0.8 }}
      >
        Baked with <br /> Love,
      </motion.h1>

      {/* Swiper Carousel */}
      <div className="relative max-w-[500px] w-full h-[300px] sm:h-[400px] md:h-[450px] lg:h-[500px] justify-self-center lg:col-start-2 lg:row-start-1">
        <Swiper
          ref={swiperRef}
          modules={[Autoplay, Navigation, Pagination]}
          slidesPerView={1}
          autoplay={{ delay: 4000, disableOnInteraction: false }}
          navigation={{ prevEl: ".hero-prev", nextEl: ".hero-next" }}
          pagination={{ el: ".hero-pagination", clickable: true }}
          loop
          className="w-full h-full"
        >
          {heroImages.map((img, index) => (
            <SwiperSlide key={img.id}>
              <motion.div
                className="relative h-full w-full flex items-center justify-center"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={inView ? { scale: 1, opacity: 1 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <LazyImage
                  src={img.image}
                  alt={img.alt}
                  isSmaller={img.id === 3} // Make the 3rd image smaller
                />
              </motion.div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      {/* Served with Joy Section */}
      <motion.div
        className="flex flex-col gap-4 text-center sm:text-left z-10
                  mt-32 sm:mt-60 lg:mt-72 xl:mt-80
                  lg:col-start-3 lg:row-start-1 lg:self-center
                  lg:-translate-x-32 px-2 sm:px-0 md:px-4"
        initial={{ opacity: 0, x: 50 }}
        animate={inView ? { opacity: 1, x: 0 } : {}}
        transition={{ duration: 0.8 }}
      >
        <h2
          className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-red-500 leading-tight"
          style={{ fontFamily: "'Comic Sans MS', cursive" }}
        >
          Served with Joy.
        </h2>
        <p className="text-sm sm:text-base md:text-lg text-[#698F8C] leading-relaxed max-w-md sm:max-w-lg lg:max-w-sm">
          Experience the joy of perfectly baked cakes, made with love and crafted to bring a smile to every celebration.
        </p>
        <motion.button
          className="px-5 sm:px-6 py-2 sm:py-3 bg-red-500 rounded-full text-white font-bold text-base sm:text-lg
                     hover:bg-red-600 hover:scale-105 active:scale-95 transition-all w-fit mx-auto sm:mx-0"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
          onClick={handleShopNowClick}
          aria-label="Navigate to menu page"
        >
          Shop Now
        </motion.button>
      </motion.div>
    </section>
  );
}